import { Block } from '@/components/base/Block'
import { Heading2 } from '@/components/base/Heading'
import { Text3, Text4 } from '@/components/base/Text'
import { showErrorToast, showToast } from '@/components/SnackBar/BaseSnackBar'
import { AppContext, useApp } from '@/hooks/useApp'
import useIsMobile from '@/hooks/useMobile'
import { useNavigator } from '@/hooks/useNavigator'
import { align, display } from '@/styles/display'
import { fontWeight } from '@/styles/font'
import spaceStyle from '@/styles/space'
import { whiteSpace } from '@/styles/white_space'
import { useIsMutating } from '@tanstack/react-query'
import {
  V1JobOfferAcceptanceStatusType,
  V1JobPostingActionType,
  type V1JobOfferAcceptanceDetailComponent
} from 'mnvm-mypage-app-ts-if'
import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  useJobPostingServicePostAction,
  jobPostingServicePostActionMutationKey
} from '../../api/postJobPostingJobSeekerAction'
import { OfferAcceptanceButtonSection } from './OfferAcceptanceButtonSection'
import { OfferAcceptanceDateSection } from './OfferAcceptanceDateSection'
import { OfferAcceptanceFileSection } from './OfferAcceptanceFileSection'
import { OfferAcceptanceMessageSection } from './OfferAcceptanceMessageSection'
import { OfferAcceptanceModal } from './OfferAcceptanceModal'

interface Props {
  component: V1JobOfferAcceptanceDetailComponent
}

const JobPostingOfferAcceptanceContainer = ({
  component
}: Props): JSX.Element => {
  const app = useApp()
  const nav = useNavigator()
  const isMobile = useIsMobile()
  const { id } = useParams<{ id: string }>()

  const jobPostingId = id ?? ''

  // POST action
  const useJobPostingServicePostActionMutation =
    useJobPostingServicePostAction()
  const isJobPostingServicePostActionMutating = Boolean(
    useIsMutating([jobPostingServicePostActionMutationKey])
  )

  // isCheckboxDisable is false when all files are downloaded
  const [isCheckboxDisable] = useState(false)
  const [checked, setChecked] = useState(false)
  const [isShowModal, setIsShowModal] = useState(false)
  const [isCancelModal, setIsCancelModal] = useState(false)

  const [isShowFullText, setIsShowFullText] = useState(false)

  const detail = component.jobOfferAcceptanceDetail
  const detailInsertDate: string = detail?.insertDate ?? ''
  const detailDueDate: string = detail?.dueDate ?? ''
  const detailRemainingDueDate: string = detail?.remainingDueDate ?? ''
  const status =
    detail?.jobOfferAcceptanceStatusType ??
    V1JobOfferAcceptanceStatusType.Unspecified
  const { dispatch } = useContext(AppContext)

  useEffect(() => {
    const spHeaderColor =
      status === V1JobOfferAcceptanceStatusType.Completed
        ? app.theme.fromAppThemeType(detail?.appThemeType)
        : undefined
    const spHeaderTitle = getScreenTitle(status)

    dispatch({
      ...app,
      headerBackroundColor: spHeaderColor,
      screenTitle: spHeaderTitle
    })
    return () => {
      dispatch({
        ...app,
        headerBackroundColor: undefined,
        screenTitle: ''
      })
    }
  }, [status])

  const onChecked = (): void => {
    setChecked(!checked)
  }

  const onShowModal = (isCancelModal: boolean): void => {
    setIsShowModal(true)
    setIsCancelModal(isCancelModal)
  }

  const onCloseModal = (): void => {
    setIsShowModal(false)
  }

  const onClickDoneButton = (): void => {
    const actionType = V1JobPostingActionType.AcceptJobOffer
    setIsShowModal(false)
    if (isJobPostingServicePostActionMutating) {
      console.log('prevent JobPostingServicePostAction because in mutating')
      return
    }

    void useJobPostingServicePostActionMutation
      .mutateAsync({
        jobPostingId,
        body: {
          actionType
        }
      })
      .then(() => {
        void nav?.goBack()
        showToast(
          `内定ご承諾の意思を承りました。
      マイナビより求人企業へご連絡をさせていただきます。
      今後の手続きについては担当キャリアアドバイザーよりご連絡いたします。`
        )
      })
      .catch(async (err) => {
        console.error(err)
        showErrorToast(
          '内定承諾に失敗しました。担当キャリアアドバイザーまでお問い合わせください。'
        )
      })
  }

  const getScreenTitle = (status: V1JobOfferAcceptanceStatusType): string => {
    switch (status) {
      case V1JobOfferAcceptanceStatusType.Pending:
        return '内定'
      case V1JobOfferAcceptanceStatusType.Completed:
        return '内定承諾済み'
      case V1JobOfferAcceptanceStatusType.Declined:
        return '内定辞退済み'

      default:
        return ''
    }
  }

  const getScreenInstruction = (
    status: V1JobOfferAcceptanceStatusType
  ): string => {
    switch (status) {
      case V1JobOfferAcceptanceStatusType.Pending:
        return 'この度はご内定おめでとうございます。\n以下の書類をご確認ください。'
      case V1JobOfferAcceptanceStatusType.Completed:
        return '内定のご承諾を承りました。 担当キャリアアドバイザー・求人企業からの 案内に沿って入社までご準備ください。'
      case V1JobOfferAcceptanceStatusType.Declined:
        return '内定のご辞退を承りました。'

      default:
        return ''
    }
  }

  return (
    <>
      <Block
        display={display.FLEX}
        alignItems={align.FLEX_START}
        textAlign={align.LEFT}
        style={{ boxSizing: 'border-box', flexDirection: 'column', gap: 8 }}
      >
        {!isMobile && status === V1JobOfferAcceptanceStatusType.Completed && (
          <Block
            width={'100%'}
            height={'34px'}
            backgroundColor={app.theme.fromAppThemeType(detail?.appThemeType)}
            style={{ alignContent: 'center' }}
          >
            <Text3
              textColor={app.theme.mainTextColor}
              fontWeight={fontWeight.Bold}
              margin={{ all: spaceStyle.NONE }}
              maxLines={1}
            >
              内定承諾済
            </Text3>
          </Block>
        )}

        <Heading2 fixedSPfontSize margin={{ bottom: spaceStyle.NONE }}>
          {detail?.jobPostingName ?? ''}
        </Heading2>
        <Text4
          textColor={app.theme.subTextColor}
          fontWeight={fontWeight.Bold}
          margin={{ all: spaceStyle.NONE }}
          maxLines={1}
        >
          求人番号： {detail?.uuid ?? ''}
        </Text4>
        <Block width={'100%'} textAlign={isMobile ? align.RIGHT : align.LEFT}>
          <Text4
            textColor={app.theme.subTextColor}
            fontWeight={fontWeight.Bold}
            margin={{ horizontal: spaceStyle.NONE, top: spaceStyle.NONE }}
            maxLines={3}
          >
            {detail?.companyName ?? ''}
          </Text4>
        </Block>
        <OfferAcceptanceDateSection
          detailInsertDate={detailInsertDate}
          detailDueDate={detailDueDate}
          detailRemainingDueDate={detailRemainingDueDate}
          status={status}
        ></OfferAcceptanceDateSection>
        <Text3
          textColor={app.theme.mainTextColor}
          margin={{
            horizontal: spaceStyle.NONE,
            top: spaceStyle.NONE,
            bottom: spaceStyle.XXXL
          }}
          whiteSpace={whiteSpace.PRE_LINE}
        >
          {getScreenInstruction(status)}
        </Text3>
        <OfferAcceptanceMessageSection
          isShowFullText={isShowFullText}
          setIsShowFullText={setIsShowFullText}
          caMessage={detail?.caMessage ?? ''}
          status={status}
        ></OfferAcceptanceMessageSection>
      </Block>

      <OfferAcceptanceFileSection
        checked={checked}
        onChecked={onChecked}
        isCheckboxDisable={isCheckboxDisable}
        files={detail?.attachmentFiles}
        status={status}
      ></OfferAcceptanceFileSection>

      {status === V1JobOfferAcceptanceStatusType.Pending ? (
        <>
          <OfferAcceptanceButtonSection
            checked={checked}
            onShowModal={onShowModal}
          ></OfferAcceptanceButtonSection>
          {isShowModal && (
            <OfferAcceptanceModal
              companyName={detail?.companyName ?? ''}
              isShowModal={isShowModal}
              isCancelModal={isCancelModal}
              onClickDoneButton={onClickDoneButton}
              onCloseModal={onCloseModal}
            ></OfferAcceptanceModal>
          )}
        </>
      ) : (
        <></>
      )}
    </>
  )
}

export default JobPostingOfferAcceptanceContainer
